import { RequestClient } from 'shared/library/api-client';
import { GenericRouteParams } from 'shared/library/routes/provider';
import { apiRoute } from 'shared/library/api-route';
import type { ProductCategory } from 'shared/@generated/models';
import { Breeder, User } from 'shared/@generated/models';
import { CountryCode, Redirect } from 'shared/@types';
import { ShopData, ShopSettings } from 'shared/state/shop';
import { monitoring } from './monitoring';

type RedirectOrNotFound = { redirect: Redirect } | { notFound: true };

export function isRedirectOrNotFound(value: unknown): value is RedirectOrNotFound {
  return (
    typeof value === 'object' && value !== null && ('redirect' in value || 'notFound' in value)
  );
}

export type PageBuilderDefaultOutput = {
  requiresAuth: boolean;
  requiresBreeder: boolean;
  routeParams: GenericRouteParams;
  shopData: ShopData;
};

export type PageDefaultProps = {
  languageCode: string;
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
} & PageBuilderDefaultOutput;

export type PageBuilderOutput<T> = RedirectOrNotFound | ({ props: T } & PageBuilderDefaultOutput);

export type PageRequestContext = {
  locale?: string;
  countryCode?: CountryCode;
  params?: Record<string, string | string[] | undefined>;
  query?: Record<string, string | string[] | undefined>;
  req?: {
    headers?: Record<string, string | string[] | undefined>;
  };
  res?: {
    setHeader?: (name: string, value: string) => void;
    end?: (body?: unknown) => void;
  };
};

export type RunContext<TQueryParams> = {
  req: RequestClient;
  params: TQueryParams;
  hasApiKey: boolean;
  ctx: PageRequestContext;
};

type RequestFn<TScreenProps, TQueryParams> = (
  p: RunContext<TQueryParams>,
) => Promise<Partial<TScreenProps> | { shopData: Partial<ShopData> } | RedirectOrNotFound>;

export class PageBuilderDataError extends Error {
  statusCode = 500;

  constructor(message: string) {
    super(message);
    this.name = 'PageBuilderDataError';
    Object.setPrototypeOf(this, PageBuilderDataError.prototype);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describeValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';

  return typeof value;
}

function isRawApiDataEnvelope(value: Record<string, unknown>): boolean {
  if (!('data' in value)) {
    return false;
  }

  const keys = Object.keys(value);
  const envelopeKeys = ['data', 'links', 'meta'];
  const hasOnlyEnvelopeKeys = keys.every((key) => envelopeKeys.includes(key));
  const hasPaginatorKeys = [
    'current_page',
    'per_page',
    'last_page',
    'next_page_url',
    'prev_page_url',
  ].some((key) => key in value);

  return keys.length === 1 || hasOnlyEnvelopeKeys || hasPaginatorKeys;
}

function buildRequestDataError(requestIndex: number, message: string) {
  return new PageBuilderDataError(`PageBuilder request #${requestIndex + 1} ${message}`);
}

function getObjectKeys(value?: Record<string, unknown>): string[] {
  return value ? Object.keys(value).sort() : [];
}

export type ResolvedPageBuilderResult<TScreenProps extends Record<string, any>> =
  | { status: 'redirect'; redirect: Redirect }
  | { status: 'notFound' }
  | { status: 'requiresAuth' }
  | { status: 'requiresBreeder' }
  | {
      status: 'ready';
      props: TScreenProps;
      defaultProps: PageDefaultProps;
    };

export function normalizePageParams<TQueryParams extends Record<string, any>>(
  rawParams: Record<string, string | string[] | undefined>,
  extraParams?: Partial<TQueryParams>,
) {
  const params = Object.fromEntries(
    Object.entries(rawParams).filter(([key, value]) => {
      if (key.startsWith('__next') || key === 'redirect' || key === 'notFound' || key === '__tab') {
        return false;
      }

      return value !== undefined;
    }),
  ) as Partial<TQueryParams>;

  return {
    ...params,
    ...extraParams,
  } as TQueryParams;
}

export async function resolvePageViewer({
  apiKey,
  user,
  breeder,
  shouldLoad,
  load,
  onUnauthorized,
}: {
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
  shouldLoad: boolean;
  load: () => Promise<{ user: User; breeder: Breeder | null }>;
  onUnauthorized?: () => void;
}) {
  if (!apiKey || !shouldLoad) {
    return {
      apiKey,
      user,
      breeder,
    };
  }

  try {
    const viewer = await load();

    return {
      apiKey,
      user: viewer.user,
      breeder: viewer.breeder,
    };
  } catch (error: any) {
    if (error?.response?.status === 401) {
      onUnauthorized?.();

      return {
        apiKey: null,
        user: null,
        breeder: null,
      };
    }

    throw error;
  }
}

export function buildPageDefaultProps<TScreenProps extends Record<string, any>>({
  result,
  languageCode,
  apiKey,
  user,
  breeder,
}: {
  result: Extract<PageBuilderOutput<TScreenProps>, { props: TScreenProps }>;
  languageCode: string;
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
}): PageDefaultProps {
  return {
    languageCode,
    apiKey,
    user,
    breeder,
    requiresAuth: result.requiresAuth,
    requiresBreeder: result.requiresBreeder,
    routeParams: result.routeParams,
    shopData: {
      productCategories: result.shopData.productCategories,
      settings: result.shopData.settings,
    },
  };
}

export function buildFallbackPageDefaultProps({
  languageCode,
  apiKey,
  user,
  breeder,
}: {
  languageCode: string;
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
}): PageDefaultProps {
  return {
    languageCode,
    apiKey,
    user,
    breeder,
    requiresAuth: false,
    requiresBreeder: false,
    routeParams: {},
    shopData: {
      productCategories: [],
      settings: null,
    },
  };
}

export function resolvePageBuilderResult<TScreenProps extends Record<string, any>>({
  result,
  languageCode,
  apiKey,
  user,
  breeder,
}: {
  result: PageBuilderOutput<TScreenProps>;
  languageCode: string;
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
}): ResolvedPageBuilderResult<TScreenProps> {
  if (isRedirectOrNotFound(result)) {
    if ('redirect' in result) {
      return {
        status: 'redirect',
        redirect: result.redirect,
      };
    }

    return {
      status: 'notFound',
    };
  }

  const defaultProps = buildPageDefaultProps({
    result,
    languageCode,
    apiKey,
    user,
    breeder,
  });

  if (defaultProps.requiresAuth && !defaultProps.apiKey) {
    return {
      status: 'requiresAuth',
    };
  }

  if (defaultProps.requiresBreeder && !defaultProps.breeder) {
    return {
      status: 'requiresBreeder',
    };
  }

  return {
    status: 'ready',
    props: result.props,
    defaultProps,
  };
}

export async function executePageBuilder<
  TScreenProps extends Record<string, any>,
  TQueryParams extends Record<string, any>,
>({
  pageBuilder,
  req,
  params,
  ctx,
  languageCode,
  apiKey,
  user,
  breeder,
  shouldLoadViewer,
  loadViewer,
  onViewerUnauthorized,
}: {
  pageBuilder?: PageBuilder<TScreenProps, TQueryParams>;
  req: RequestClient;
  params: TQueryParams;
  ctx: PageRequestContext;
  languageCode: string;
  apiKey: string | null;
  user: User | null;
  breeder: Breeder | null;
  shouldLoadViewer: boolean;
  loadViewer: () => Promise<{ user: User; breeder: Breeder | null }>;
  onViewerUnauthorized?: () => void;
}) {
  const viewer = await resolvePageViewer({
    apiKey,
    user,
    breeder,
    shouldLoad: shouldLoadViewer,
    load: loadViewer,
    onUnauthorized: onViewerUnauthorized,
  });

  const result = await (pageBuilder ?? new PageBuilder<TScreenProps, TQueryParams>()).run({
    req,
    params,
    hasApiKey: !!viewer.apiKey,
    ctx,
  });

  return resolvePageBuilderResult({
    result,
    languageCode,
    apiKey: viewer.apiKey,
    user: viewer.user,
    breeder: viewer.breeder,
  });
}

export class PageBuilder<
  TScreenProps extends Record<string, any> = {},
  TQueryParams extends Record<string, any> = {},
> {
  private _requiresAuth = false;
  private _requiresBreeder = false;
  private _routeParams: GenericRouteParams = {};
  private _routeParamsDeriver?: (props: TScreenProps) => GenericRouteParams;
  private _translationPaths?: string[];

  private propsTransformer?: (
    data: RunContext<TQueryParams> & {
      props: TScreenProps;
    },
  ) => TScreenProps | RedirectOrNotFound;

  private paramsTransformer?: (params: TQueryParams) => TQueryParams | RedirectOrNotFound;

  requests: RequestFn<TScreenProps, TQueryParams>[] = [];

  constructor() {}

  requiresAuth() {
    this._requiresAuth = true;
    return this;
  }

  requiresBreeder() {
    this._requiresAuth = true;
    this._requiresBreeder = true;
    return this;
  }

  request(fn: RequestFn<TScreenProps, TQueryParams>) {
    this.requests.push(fn);
    return this;
  }

  includeShopData() {
    this.request(async ({ req }) => {
      const [categoriesResponse, settingsResponse] = await Promise.all([
        req<{ product_categories: ProductCategory[] }>(apiRoute('product-categories.index')),
        req<{ settings: ShopSettings }>(apiRoute('shop.settings')),
      ]);

      return {
        shopData: {
          productCategories: categoriesResponse.product_categories,
          settings: settingsResponse.settings,
        },
      };
    });

    return this;
  }

  prepareParams(fn: typeof this.paramsTransformer) {
    this.paramsTransformer = fn;
    return this;
  }

  transformProps(fn: typeof this.propsTransformer) {
    this.propsTransformer = fn;
    return this;
  }

  routeParams(params: GenericRouteParams) {
    this._routeParams = params;
    return this;
  }

  translationPaths(paths: string[]) {
    this._translationPaths = paths;
    return this;
  }

  getTranslationPaths() {
    return this._translationPaths;
  }

  deriveRouteParams(fn: (props: TScreenProps) => GenericRouteParams) {
    this._routeParamsDeriver = fn;
    return this;
  }

  async run(runCtx: RunContext<TQueryParams>): Promise<PageBuilderOutput<TScreenProps>> {
    const params = this.prepareParamsIfNeeded(runCtx.params);

    if (isRedirectOrNotFound(params)) {
      return params;
    }

    runCtx.params = params;

    // Guests on protected pages are sent to login; their requests would only fail with 401/403.
    if (this._requiresAuth && !runCtx.hasApiKey) {
      return {
        props: {} as TScreenProps,
        requiresAuth: true,
        requiresBreeder: this._requiresBreeder,
        routeParams: {},
        shopData: { productCategories: [], settings: null },
      };
    }

    const partials = await Promise.all(
      this.requests.map((request, index) => this.runRequest(request, index, runCtx)),
    );

    const redirectOrNotFound = partials.find(isRedirectOrNotFound);

    if (redirectOrNotFound) {
      return redirectOrNotFound;
    }

    const mergedProps = Object.assign({}, ...partials) as TScreenProps;

    if (isRedirectOrNotFound(mergedProps)) {
      return mergedProps;
    }

    const transformedProps = this.validatePageProps(
      this.transformPropsIfNeeded(mergedProps, runCtx),
    );

    if (isRedirectOrNotFound(transformedProps)) {
      return transformedProps;
    }

    const routeParams = this._routeParamsDeriver
      ? this._routeParamsDeriver(transformedProps)
      : this._routeParams;

    const shopDataFromTransformedProps =
      'shopData' in transformedProps ? (transformedProps.shopData as Partial<ShopData>) : undefined;

    return {
      props: transformedProps,
      requiresAuth: this._requiresAuth,
      requiresBreeder: this._requiresBreeder,
      routeParams,
      shopData: {
        productCategories: shopDataFromTransformedProps?.productCategories ?? [],
        settings: shopDataFromTransformedProps?.settings ?? null,
      },
    };
  }

  private async runRequest(
    request: RequestFn<TScreenProps, TQueryParams>,
    index: number,
    runCtx: RunContext<TQueryParams>,
  ) {
    try {
      const result = await request(runCtx);

      return this.validateRequestResult(result, index);
    } catch (error: any) {
      const pageBuilderRequest = {
        index,
        request: index + 1,
        params: getObjectKeys(runCtx.params),
        hasApiKey: runCtx.hasApiKey,
        locale: runCtx.ctx.locale,
      };

      error.pageBuilderRequest = pageBuilderRequest;
      monitoring.Sentry.setContext('page_builder_request', pageBuilderRequest);

      throw error;
    }
  }

  private validateRequestResult(result: unknown, requestIndex: number) {
    if (isRedirectOrNotFound(result)) {
      return result;
    }

    if (!isRecord(result)) {
      throw buildRequestDataError(
        requestIndex,
        `returned ${describeValue(result)} instead of props`,
      );
    }

    if (isRawApiDataEnvelope(result)) {
      throw buildRequestDataError(
        requestIndex,
        'returned a raw API data envelope. Map API responses to named page props before returning them.',
      );
    }

    if (Object.keys(result).length === 0) {
      throw buildRequestDataError(
        requestIndex,
        'returned no props. Return named page props, shopData, redirect, or notFound.',
      );
    }

    const undefinedProps = Object.entries(result)
      .filter(([, value]) => value === undefined)
      .map(([key]) => key);

    if (undefinedProps.length > 0) {
      throw buildRequestDataError(
        requestIndex,
        `returned undefined for ${undefinedProps.map((key) => `"${key}"`).join(', ')}`,
      );
    }

    return result;
  }

  private validatePageProps(
    props: TScreenProps | RedirectOrNotFound,
  ): TScreenProps | RedirectOrNotFound {
    if (isRedirectOrNotFound(props)) {
      return props;
    }

    if (!isRecord(props)) {
      throw new PageBuilderDataError(
        `PageBuilder transformed props must be an object, got ${describeValue(props)}`,
      );
    }

    const undefinedProps = Object.entries(props)
      .filter(([, value]) => value === undefined)
      .map(([key]) => key);

    if (undefinedProps.length > 0) {
      throw new PageBuilderDataError(
        `PageBuilder resolved undefined page props: ${undefinedProps
          .map((key) => `"${key}"`)
          .join(', ')}`,
      );
    }

    return props;
  }

  private prepareParamsIfNeeded(params: TQueryParams): TQueryParams | RedirectOrNotFound {
    if (!this.paramsTransformer) {
      return params;
    }

    return this.paramsTransformer(params);
  }

  private transformPropsIfNeeded(
    props: TScreenProps,
    runCtx: RunContext<TQueryParams>,
  ): TScreenProps | RedirectOrNotFound {
    if (!this.propsTransformer) {
      return props;
    }

    return this.propsTransformer({ props, ...runCtx });
  }
}
